import * as React from "react";
import {
  Header,
  type ScrollHeaderProps,
} from "@codeherence/react-native-header";
import Animated, {
  interpolate,
  useAnimatedStyle,
} from "react-native-reanimated";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";

import BellButton from "@/Components/Headers/Components/BellButton";
import { useSession } from "@/context/auth/useSession";
import { Spacings } from "@/styling/spacings";
import { type UserImageMedia } from "@/schemas/user/image";
import DrawerMenuButton from "@/Components/Headers/Components/DrawerMenuButton";

const ROOT_HORIZONTAL_PADDING = 12;

type Props = {
  background: UserImageMedia | null;
  largeHeaderHeight: number;
};

function AccountHeader({
  showNavBar,
  scrollY,
  background,
  largeHeaderHeight,
}: ScrollHeaderProps & Props): React.JSX.Element {
  const { session } = useSession();

  const { top, left, right } = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const smallHeaderHeight = Math.min(top + 48, 110);

  const [bannerHeight, setBannerHeight] = React.useState(
    largeHeaderHeight + smallHeaderHeight,
  );
  const bannerTranslationStyle = useAnimatedStyle(() => {
    const bannerTranslation = interpolate(
      scrollY.value,
      [0, largeHeaderHeight],
      [0, -largeHeaderHeight],
      "clamp",
    );

    return { transform: [{ translateY: bannerTranslation }] };
  }, [largeHeaderHeight]);

  const animatedScaleStyle = useAnimatedStyle(() => {
    const bannerHeightRatio = height / bannerHeight;

    const scaleY = interpolate(
      scrollY.value,
      [0, -(height + bannerHeight)],
      [1, bannerHeightRatio],
      "clamp",
    );

    return {
      transform: [{ scaleY }, { scaleX: scaleY }],
    };
  }, [height, bannerHeight]);
  return (
    <View style={[styles.container, { minHeight: smallHeaderHeight }]}>
      {/* background image */}
      <Animated.View style={[StyleSheet.absoluteFill, bannerTranslationStyle]}>
        <Animated.View
          onLayout={(e) => setBannerHeight(e.nativeEvent.layout.height)}
          style={animatedScaleStyle}
        >
          <View
            style={{
              height: largeHeaderHeight + smallHeaderHeight,
              marginBottom: -largeHeaderHeight,
            }}
          >
            <Animated.View
              style={[StyleSheet.absoluteFill, styles.darkOverlay]}
            />
            {background ? (
              <Image
                source={{ uri: background.attributes.previewUrl }}
                contentFit="cover"
                contentPosition="center"
                style={[styles.imageStyle, { width }]}
              />
            ) : (
              <LinearGradient
                colors={["#4c669f", "#3b5998", "#192f6a", "#192f6a", "#0f1d42"]}
                locations={[0, 0.25, 0.5, 0.75, 1]}
                style={[styles.imageStyle, { width }]}
              />
            )}
          </View>
        </Animated.View>
      </Animated.View>
      {/* header */}
      <Header
        showNavBar={showNavBar}
        headerStyle={styles.headerStyle}
        noBottomBorder
        // header left
        headerLeft={
          <>
            <DrawerMenuButton style={styles.headerButtonStyle} />
            <Text style={styles.title}>Account</Text>
          </>
        }
        headerLeftStyle={[
          styles.headerLeftContainer,
          { paddingLeft: Math.max(left, ROOT_HORIZONTAL_PADDING) },
        ]}
        // header right
        headerRight={
          session && (
            <BellButton
              hasPendingNotifications={
                session.identity.unreadNotificationsCount > 0
              }
              style={styles.headerButtonStyle}
            />
          )
        }
        headerRightStyle={[
          styles.headerRightContainer,
          { paddingRight: Math.max(right, ROOT_HORIZONTAL_PADDING) },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  darkOverlay: {
    zIndex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
  },
  imageStyle: {
    height: "100%",
  },
  headerStyle: {
    backgroundColor: "transparent",
  },
  headerLeftContainer: {
    gap: Spacings.s4,
    width: "80%",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "rgba(255, 255, 255, 0.95)",
  },
  headerRightContainer: {
    paddingLeft: Spacings.s3,
    width: "20%",
  },
  headerButtonStyle: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 100,
    padding: 7,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99,
  },
});

export default AccountHeader;
